import { BlockSuiteError, ErrorCode } from '@blocksuite/global/exceptions';

import type { BlockProps } from '../model';
import type { BlobCRUD } from './type';

/** Import-owned source. Managers only read these maps; cleanup owns local maps. */
export type AssetSource = {
  readonly assets: ReadonlyMap<string, Blob>;
  readonly paths: ReadonlyMap<string, string>;
};

type AssetsManagerConfig = {
  blob: BlobCRUD;
  source?: AssetSource;
};

/** The first free `stem (n).ext`; a name without an extension gets `stem (n)`. */
function makeNewNameWhenConflict(names: Set<string>, name: string) {
  const dot = name.lastIndexOf('.');
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : '';
  for (let i = 1; ; i++) {
    const candidate = `${stem} (${i})${ext}`;
    if (!names.has(candidate)) return candidate;
  }
}

export class AssetsManager {
  // `blockId` is the key.
  readonly uploadingAssetsMap = new Map<
    string,
    {
      blob: Blob;
      abortController?: AbortController;
      mapInto: (blobId: string) => Partial<BlockProps>;
    }
  >();

  private readonly _assetsMap = new Map<string, Blob>();

  private readonly _blob: BlobCRUD;

  private readonly _source?: AssetSource;

  private readonly _names = new Set<string>();

  private readonly _pathBlobIdMap = new Map<string, string>();

  constructor(options: AssetsManagerConfig) {
    this._blob = options.blob;
    this._source = options.source;
  }

  cleanup() {
    this._assetsMap.clear();
    this._names.clear();
  }

  /** Resolve a blob without copying the shared source into this job. */
  getAsset(blobId: string) {
    return this._assetsMap.get(blobId) ?? this._source?.assets.get(blobId);
  }

  /** Resolve an archive path; local entries take precedence. */
  getBlobId(path: string) {
    return this._pathBlobIdMap.get(path) ?? this._source?.paths.get(path);
  }

  getAssets() {
    return this._assetsMap;
  }

  getPathBlobIdMap() {
    return this._pathBlobIdMap;
  }

  isEmpty() {
    return this._assetsMap.size === 0 && !this._source?.assets.size;
  }

  async readFromBlob(blobId: string) {
    if (this._assetsMap.has(blobId)) return;
    const blob =
      this._source?.assets.get(blobId) ?? (await this._blob.get(blobId));
    if (!blob) {
      console.error(`Blob ${blobId} not found in blob manager`);
      return;
    }
    if (blob instanceof File) {
      let file = blob;
      if (this._names.has(blob.name)) {
        const newName = makeNewNameWhenConflict(this._names, blob.name);
        file = new File([blob], newName, { type: blob.type });
      }
      this._assetsMap.set(blobId, file);
      this._names.add(file.name);
      return;
    }
    if (blob.type && blob.type !== 'application/octet-stream') {
      this._assetsMap.set(blobId, blob);
      return;
    }
    // Guess the file type from the buffer
    const buffer = await blob.arrayBuffer();
    const FileType = await import('file-type');
    const fileType = await FileType.fileTypeFromBuffer(buffer);
    if (fileType) {
      const file = new File([blob], '', { type: fileType.mime });
      this._assetsMap.set(blobId, file);
      return;
    }
    this._assetsMap.set(blobId, blob);
  }

  async writeToBlob(blobId: string) {
    const blob = this.getAsset(blobId);
    if (!blob) {
      throw new BlockSuiteError(
        ErrorCode.TransformerError,
        `Blob ${blobId} not found in assets manager`
      );
    }

    await this._blob.set(blobId, blob);
  }
}
